import type { BaseUser } from "@models";
import type { Suggestion } from "@tiptap-pro/extension-tracked-changes";
import type { JSONContent } from "@tiptap/core";
import { format, isToday } from "date-fns";
import type { TiptapComment, TiptapCommentThread } from "./types";

const FALLBACK_DATE = new Date(0);
const DOVETAIL_AGENT_COMMENT_SOURCE = "dovetail-agent";
const DEFAULT_AGENT_NAME = "Dovetail";
const DEFAULT_AGENT_USER_ID = "dovetail";
const DOVETAIL_AGENT_USER_IDS = new Set([
  DEFAULT_AGENT_USER_ID,
  "dovetail-agent",
]);
const DOVETAIL_AGENT_AVATAR_SRC = "/favicon.svg";

const isJSONContent = (value: unknown): value is JSONContent =>
  typeof value === "object" && value !== null && "type" in value;

const getNodeText = (node: JSONContent): string => {
  const childText =
    node.content?.map(getNodeText).filter(Boolean).join(" ") ?? "";

  return [node.text, childText]
    .filter((value): value is string => typeof value === "string" && !!value)
    .join(" ");
};

const normalizeUserId = (value: string | null | undefined) => {
  const trimmedValue = value?.trim();

  return trimmedValue || null;
};

const isDovetailAgentUserId = (value: string | null | undefined) => {
  const userId = normalizeUserId(value);

  return userId ? DOVETAIL_AGENT_USER_IDS.has(userId) : false;
};

const normalizeText = (value: string | null | undefined) => {
  const trimmedValue = value?.trim();

  return trimmedValue || null;
};

const getFlatTiptapCommentDataEmail = (
  data: TiptapComment["data"] | TiptapCommentThread["data"] | undefined
) => {
  if (!data || !("email" in data)) {
    return null;
  }

  return typeof data.email === "string" ? data.email : null;
};

const getTiptapCommentDataDisplayName = (
  data: TiptapComment["data"] | TiptapCommentThread["data"] | undefined
) =>
  normalizeText(data?.user?.name) ??
  normalizeText(data?.userName) ??
  normalizeText(data?.user?.email) ??
  normalizeText(getFlatTiptapCommentDataEmail(data));

const getTiptapCommentMetadataDisplayName = (
  comment: TiptapComment,
  thread: TiptapCommentThread
) =>
  getTiptapCommentDataDisplayName(comment.data) ??
  getTiptapCommentDataDisplayName(thread.data);

const getTiptapThreadAuthorId = (thread: TiptapCommentThread): string | null =>
  normalizeUserId(thread.data?.user?.id) ??
  normalizeUserId(thread.data?.userId);

const getTiptapCommentOwnAuthorId = (comment: TiptapComment): string | null =>
  normalizeUserId(comment.data?.user?.id) ??
  normalizeUserId(comment.data?.userId) ??
  normalizeUserId(comment.userId);

const getDovetailAgentMetadataUserId = (
  comment: TiptapComment,
  thread: TiptapCommentThread
) =>
  [
    comment.data?.user?.id,
    comment.data?.userId,
    comment.userId,
    thread.data?.user?.id,
    thread.data?.userId,
  ].find(isDovetailAgentUserId) ?? null;

const getTiptapCommentAgentUserId = (
  comment: TiptapComment,
  thread: TiptapCommentThread
) =>
  normalizeUserId(comment.data?.agentUserId) ??
  normalizeUserId(thread.data?.agentUserId) ??
  getDovetailAgentMetadataUserId(comment, thread) ??
  DEFAULT_AGENT_USER_ID;

const hasDovetailAgentCommentMarker = (comment: TiptapComment) =>
  comment.data?.actor === DOVETAIL_AGENT_COMMENT_SOURCE ||
  comment.data?.source === DOVETAIL_AGENT_COMMENT_SOURCE ||
  Boolean(comment.data?.agentUserId) ||
  isDovetailAgentUserId(getTiptapCommentOwnAuthorId(comment));

const hasDovetailAgentThreadMarker = (thread: TiptapCommentThread) =>
  thread.data?.actor === DOVETAIL_AGENT_COMMENT_SOURCE ||
  thread.data?.source === DOVETAIL_AGENT_COMMENT_SOURCE ||
  Boolean(thread.data?.agentUserId) ||
  isDovetailAgentUserId(getTiptapThreadAuthorId(thread));

export const getTiptapCommentContentText = (content: unknown): string => {
  if (typeof content === "string") {
    return content;
  }

  if (isJSONContent(content)) {
    return getNodeText(content);
  }

  if (Array.isArray(content)) {
    return content
      .map((item) => (isJSONContent(item) ? getNodeText(item) : ""))
      .filter(Boolean)
      .join(" ");
  }

  return "";
};

export const parseTiptapCollaborationDate = (
  value: string | undefined
): Date | null => {
  if (!value) {
    return null;
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
};

export const toTiptapCommentDate = (value: string | undefined): Date =>
  parseTiptapCollaborationDate(value) ?? FALLBACK_DATE;

export const formatTiptapCollaborationTime = (date: Date) =>
  isToday(date)
    ? `${format(date, "HH:mm")} Today`
    : format(date, "dd/MM/yyyy HH:mm");

export const getTiptapCommentAuthorId = (
  comment: TiptapComment,
  thread: TiptapCommentThread
): string | null =>
  getTiptapCommentOwnAuthorId(comment) ?? getTiptapThreadAuthorId(thread);

export const isDovetailAgentTiptapComment = (
  comment: TiptapComment,
  thread: TiptapCommentThread
) => {
  if (hasDovetailAgentCommentMarker(comment)) {
    return true;
  }

  if (getTiptapCommentOwnAuthorId(comment)) {
    return false;
  }

  return hasDovetailAgentThreadMarker(thread);
};

export const findTiptapCommentAuthor = (
  orgUsers: BaseUser[],
  authorId: string | null
) => {
  if (!authorId) {
    return null;
  }

  return orgUsers.find((user) => user.id === authorId) ?? null;
};

export const getVisibleTiptapThreadComments = (thread: TiptapCommentThread) =>
  thread.comments.filter((comment) => !comment.deletedAt);

export const getTiptapSuggestionThreadSuggestionId = (
  thread: TiptapCommentThread
) => normalizeUserId(thread.data?.suggestionId);

export const isTiptapSuggestionCommentThread = (thread: TiptapCommentThread) =>
  thread.data?.source === "suggestion" ||
  Boolean(getTiptapSuggestionThreadSuggestionId(thread));

export const isTiptapUserCommentThread = (thread: TiptapCommentThread) =>
  !isTiptapSuggestionCommentThread(thread);

export const getVisibleTiptapSuggestionThreadComments = (
  thread: TiptapCommentThread
) =>
  getVisibleTiptapThreadComments(thread).filter((comment) =>
    Boolean(getTiptapCommentContentText(comment.content).trim())
  );

export const getTiptapSuggestionThreadBySuggestionId = (
  threads: TiptapCommentThread[]
) => {
  const suggestionThreads = new Map<string, TiptapCommentThread>();

  for (const thread of threads) {
    if (thread.deletedAt || !isTiptapSuggestionCommentThread(thread)) {
      continue;
    }

    const suggestionId = getTiptapSuggestionThreadSuggestionId(thread);

    if (suggestionId) {
      suggestionThreads.set(suggestionId, thread);
    }
  }

  return suggestionThreads;
};

export const getVisibleTiptapCommentThreads = (
  threads: TiptapCommentThread[]
) =>
  threads.filter(
    (thread) =>
      isTiptapUserCommentThread(thread) &&
      !thread.deletedAt &&
      getVisibleTiptapThreadComments(thread).length > 0
  );

export const getTiptapCollaborationActivityCount = ({
  includeResolvedComments = true,
  suggestions,
  threads,
}: {
  includeResolvedComments?: boolean;
  suggestions: Suggestion[];
  threads: TiptapCommentThread[];
}) => {
  const visibleThreads = getVisibleTiptapCommentThreads(threads);
  const commentCount = includeResolvedComments
    ? visibleThreads.length
    : visibleThreads.filter((thread) => !thread.resolvedAt).length;

  return commentCount + suggestions.length;
};

export const formatTiptapCommentAuthorName = (user: BaseUser | null) => {
  if (!user) {
    return "Unknown author";
  }

  const fullName = [user.firstName, user.lastName].filter(Boolean).join(" ");

  return fullName || user.email;
};

type TiptapCommentAuthorDisplay = {
  authorName: string;
  avatarName: string;
  avatarSrc: string | null;
  avatarUserId: string | null;
  coAuthorName?: string;
  isAgentAuthored: boolean;
  onBehalfOfName: string | null;
};

export const getTiptapCommentAuthorDisplay = ({
  comment,
  orgUsers,
  thread,
}: {
  comment: TiptapComment;
  orgUsers: BaseUser[];
  thread: TiptapCommentThread;
}): TiptapCommentAuthorDisplay => {
  const authorId = getTiptapCommentAuthorId(comment, thread);
  const isAgentAuthored = isDovetailAgentTiptapComment(comment, thread);

  if (isAgentAuthored) {
    const agentName =
      normalizeText(comment.data?.agentName) ??
      normalizeText(thread.data?.agentName) ??
      getTiptapCommentMetadataDisplayName(comment, thread) ??
      DEFAULT_AGENT_NAME;
    const agentUserId = getTiptapCommentAgentUserId(comment, thread);
    const primaryAuthorUserId = [
      comment.data?.onBehalfOfUserId,
      thread.data?.onBehalfOfUserId,
      comment.data?.userId,
      comment.data?.user?.id,
      comment.userId,
      thread.data?.userId,
      thread.data?.user?.id,
    ]
      .map(normalizeUserId)
      .find(
        (userId) =>
          userId && userId !== agentUserId && !isDovetailAgentUserId(userId)
      );
    const primaryAuthor = findTiptapCommentAuthor(
      orgUsers,
      primaryAuthorUserId ?? null
    );
    if (primaryAuthor) {
      const authorName = formatTiptapCommentAuthorName(primaryAuthor);

      return {
        authorName,
        avatarName: authorName,
        avatarSrc: null,
        avatarUserId: primaryAuthor.id,
        coAuthorName: agentName,
        isAgentAuthored,
        onBehalfOfName: null,
      };
    }

    const threadAuthorId = getTiptapThreadAuthorId(thread);
    const getOnBehalfOfFallbackId = (userId: string | null) =>
      userId && userId !== agentUserId && !isDovetailAgentUserId(userId)
        ? userId
        : null;
    const onBehalfOfUserId =
      normalizeUserId(comment.data?.onBehalfOfUserId) ??
      normalizeUserId(thread.data?.onBehalfOfUserId) ??
      getOnBehalfOfFallbackId(authorId) ??
      getOnBehalfOfFallbackId(threadAuthorId);
    const onBehalfOfUser = findTiptapCommentAuthor(orgUsers, onBehalfOfUserId);
    const onBehalfOfName = onBehalfOfUser
      ? formatTiptapCommentAuthorName(onBehalfOfUser)
      : null;

    return {
      authorName: agentName,
      avatarName: agentName,
      avatarSrc: DOVETAIL_AGENT_AVATAR_SRC,
      avatarUserId: agentUserId,
      isAgentAuthored,
      onBehalfOfName,
    };
  }

  const author = findTiptapCommentAuthor(orgUsers, authorId);
  const commentOwnAuthorId = getTiptapCommentOwnAuthorId(comment);
  const authorName =
    (author ? formatTiptapCommentAuthorName(author) : null) ??
    getTiptapCommentDataDisplayName(comment.data) ??
    (commentOwnAuthorId
      ? null
      : getTiptapCommentDataDisplayName(thread.data)) ??
    formatTiptapCommentAuthorName(null);

  return {
    authorName,
    avatarName: authorName,
    avatarSrc: null,
    avatarUserId: author?.id ?? authorId,
    isAgentAuthored,
    onBehalfOfName: null,
  };
};
